import React, { useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { SharedValue, useDerivedValue, useSharedValue } from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import { TestIDs } from 'src/constants/testIDs.constants';
import { adjustCropsItemTestID, productPreviewTestID } from 'src/helpers/testIDs.helper';
import { IUserImageAreaModel } from 'src/store/_models/userImageArea';
import {
  CropPreviewContent,
  CropPreviewContentHandle,
} from 'src/components/preview-content/crop-preview-content/CropPreviewContent';
import ResponsiveScaleProvider from 'src/contexts/ResponsiveScaleProvider';
import { ContainerPreview } from '../ContainerPreview';
import { useFocus } from 'src/hooks/useFocus';
import {
  ADJUST_CROPS_ITEM_FOCUS_ORDER_STEP,
  FocusGroups,
} from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { DEFAULT_FOCUS_RING } from 'src/theme-v2/focus';

// Pixel offset applied per key press when panning the image with the arrow keys.
const KEYBOARD_PAN_STEP = 10;

const ARROW_KEY_DELTAS: Record<string, [number, number]> = {
  ArrowUp: [0, -KEYBOARD_PAN_STEP],
  ArrowDown: [0, KEYBOARD_PAN_STEP],
  ArrowLeft: [-KEYBOARD_PAN_STEP, 0],
  ArrowRight: [KEYBOARD_PAN_STEP, 0],
};

type CropPreviewProps = {
  userImageArea: IUserImageAreaModel;
  index: number;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  itemSizeSV: SharedValue<{ width: number; height: number; paddingHorizontal: number }>;
  onPanStart?: () => void;
  onPanEnd?: () => void;
};

const CropPreview = observer(
  ({
    userImageArea,
    index,
    disabled,
    editable,
    showGrid,
    itemSizeSV,
    disableSwapImageAnimation = false,
    onPanStart,
    onPanEnd,
  }: CropPreviewProps) => {
    const userImageAreaViewBox = {
      width: userImageArea?.width as number,
      height: userImageArea?.height as number,
    };
    const previewSizeSV = useSharedValue<{ width: number; height: number }>({
      width: 0,
      height: 0,
    });
    useDerivedValue(() => {
      const width = itemSizeSV.value.width - itemSizeSV.value.paddingHorizontal * 2;
      previewSizeSV.value = { width: width, height: width };
    }, [itemSizeSV]);

    const focusOrderBase = index * ADJUST_CROPS_ITEM_FOCUS_ORDER_STEP;
    // Keyed by index, not userImageArea.id: the underlying data can reuse the same
    // area id across multiple grid items, which would collide two tiles onto one focus entry.
    const focusId = adjustCropsItemTestID(index);
    const ref = useRef<HTMLElement | null>(null);
    const contentRef = useRef<CropPreviewContentHandle>(null);
    const { focused } = useFocus({
      id: focusId,
      ref,
      order: focusOrderBase,
      group: FocusGroups.MODAL,
      focusable: true,
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      const delta = ARROW_KEY_DELTAS[event.code];
      if (!delta) return;
      event.preventDefault();
      event.stopPropagation();
      contentRef.current?.nudge(delta[0], delta[1]);
    };

    return (
      // PREVIEW CONTAINER
      <View
        ref={ref as any}
        tabIndex={0}
        // ModalBody blurs any element that receives real focus inside the modal (a hack for
        // its close button) unless the element stops that focus event from bubbling to it.
        onFocus={(e: any) => e.stopPropagation()}
        {...({ onKeyDown: handleKeyDown } as any)}
        style={[styles.previewContainer, focused && DEFAULT_FOCUS_RING]}
        testID={productPreviewTestID(userImageArea.productId, TestIDs.FULLSCREEN)}
      >
        {/* title placeholder */}
        <View style={{ height: 24 }} />
        <View
          style={{
            width: '100%',
            aspectRatio: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ContainerPreview shouldUseContainerDimensions={false}>
            <ResponsiveScaleProvider
              containerViewBox={userImageAreaViewBox}
              viewBox={userImageAreaViewBox}
              containerDimensionsSV={previewSizeSV}
            >
              <CropPreviewContent
                ref={contentRef}
                userImageArea={userImageArea}
                focusOrderBase={focusOrderBase}
                focusIdPrefix={focusId}
                editable={editable}
                showGrid={showGrid}
                disabled={disabled}
                disableSwapImageAnimation={disableSwapImageAnimation}
                onPanStart={onPanStart}
                onPanEnd={onPanEnd}
              />
            </ResponsiveScaleProvider>
          </ContainerPreview>
        </View>
        {/* zoom placeholder */}
        <View style={{ height: 48 }} />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  previewContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  title: {
    //marginBottom: 12,
  },
});

export default CropPreview;
